import React from "react";
import { Globe, ArrowRight } from "lucide-react";

const n = (v) => (v ?? 0).toLocaleString("es-ES");
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function Step({ label, value, sub, rate, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {rate !== undefined && (
        <div className="flex flex-col items-center text-clay-400 w-10">
          <ArrowRight size={12}/>
          <span className="text-[10px] tabular text-clay-600">{pct(rate)}</span>
        </div>
      )}
      <div className="min-w-[88px]">
        <div className="text-[10px] uppercase tracking-widest text-clay-500">{label}</div>
        <div className="tabular font-semibold text-clay-900 text-base leading-tight">{n(value)}</div>
        {sub && <div className="text-[10px] text-clay-500 tabular">{sub}</div>}
      </div>
    </div>
  );
}

/** Hub (portada) → Landing → Itinerario → Reserve → Holds → Pagados. Hub and
 *  landing numbers arrive from the hub via POST /api/hub/v1/events. */
export function FunnelByDestination({ funnel }) {
  if (!funnel) return null;
  const { hub, landing, destinations, enabled } = funnel;
  return (
    <div className="border border-clay-300 bg-white" data-testid="funnel-by-destination">
      <div className="flex items-start justify-between gap-4 px-4 pt-4 pb-3 border-b border-clay-200">
        <div>
          <div className="smallcaps">Embudo por destino</div>
          <div className="text-[12px] text-clay-600 mt-1">
            Hub → Landing → Itinerario → Reserve → Holds → Pagados. Los % son el paso entre etapas (visitantes únicos).
          </div>
        </div>
        <div className="flex items-center gap-6 text-right" data-testid="funnel-hub-kpis">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-500 inline-flex items-center gap-1"><Globe size={10}/> Portada del hub</div>
            <div className="tabular font-semibold text-clay-900">{n(hub.visitors)} <span className="text-[11px] font-normal text-clay-500">visitantes · {n(hub.views)} visitas</span></div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-clay-500">Landings (todas)</div>
            <div className="tabular font-semibold text-clay-900">{n(landing.visitors)} <span className="text-[11px] font-normal text-clay-500">· {pct(landing.rate_from_hub)} del hub</span></div>
          </div>
        </div>
      </div>

      {!enabled && (
        <div className="px-4 py-3 text-[12px] text-amber-800 bg-amber-50 border-b border-amber-100" data-testid="funnel-hub-pending">
          El hub todavía no nos envía sus visitas (portada y landings): esas dos columnas saldrán a 0 hasta que active el beacon.
          Las columnas Itinerario → Pagados ya son reales.
        </div>
      )}

      {destinations.length === 0 ? (
        <div className="px-4 py-6 text-sm text-clay-500">Sin datos en este periodo.</div>
      ) : (
        <div className="divide-y divide-clay-100">
          {destinations.map((d) => (
            <div key={d.curated_trip_id || d.landing_slug} className="px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid={`funnel-row-${d.curated_trip_id || d.landing_slug}`}>
              <div className="w-56 shrink-0">
                <div className="font-medium text-clay-900 text-[13px] leading-tight">{d.name}</div>
                {d.landing_slug && <div className="text-[10px] text-clay-500">/landing/{d.landing_slug}</div>}
              </div>
              <Step label="Landing" value={d.landing_visitors} sub={`${n(d.landing_views)} visitas`} testid={`funnel-landing-${d.curated_trip_id || d.landing_slug}`}/>
              <Step label="Itinerario" value={d.itinerary_visitors} sub={`${n(d.itinerary_from_landing)} desde landing`} rate={d.rates.landing_to_itinerary}/>
              <Step label="Reserve" value={d.reserve_visitors} rate={d.rates.itinerary_to_reserve}/>
              <Step label="Holds" value={d.holds} rate={d.rates.reserve_to_hold}/>
              <Step label="Pagados" value={d.paid} sub={`${n(d.seats_paid)} plazas`} rate={d.rates.hold_to_paid}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
